import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, Send, AlertTriangle } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Employees can't create users, so they ask an admin to. Service and AWB come
// from the shipment; the employee only supplies the user's details.
const RequestCustomerModal = ({ shipment, notice, onClose, onSent }) => {
  const [form, setForm] = useState({
    name: shipment?.exporterName || shipment?.customerName || '',
    email: '',
    phoneNumber: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '', api: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (form.name.trim().length < 2) next.name = 'Enter the user\'s name';
    if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email address';
    if (!/^[0-9]{10}$/.test(form.phoneNumber)) next.phoneNumber = 'Phone number must be 10 digits';
    if (Object.keys(next).length) { setErrors(next); return; }

    setSaving(true);
    try {
      const res = await apiCall(API_ENDPOINTS.CUSTOMER_REQUESTS, {
        method: 'POST',
        body: JSON.stringify({
          service: shipment.shipmentType,
          awbNumber: shipment.awbNumber,
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phNum: `+91${form.phoneNumber}`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setErrors({ api: data?.message || `Could not send the request to your admin (HTTP ${res.status})` });
        return;
      }
      toast.success('Sent to your admin for approval');
      onSent?.(data?.data);
      onClose();
    } catch (err) {
      console.error('Customer request error:', err);
      setErrors({ api: 'Unable to reach the server. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (name) => `w-full px-3.5 py-2.5 rounded-xl text-[13px] font-semibold outline-none transition bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} noValidate onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden bg-white border border-slate-200 dark:bg-[#0b1220] dark:border-slate-800">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h3 className="text-[16px] font-bold text-slate-900 dark:text-white leading-tight">Ask Admin to Create User</h3>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">AWB {shipment?.awbNumber} · your admin reviews this and creates the user</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {notice && (
            <div className="flex gap-2.5 px-3.5 py-3 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-700">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 dark:text-amber-200 leading-snug">{notice}</p>
            </div>
          )}

          <div>
            <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} disabled={saving}
              placeholder="Full name as on the LEO" autoComplete="off" className={inputClass('name')} />
            {errors.name && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">Email <span className="text-red-500">*</span></label>
            <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} disabled={saving}
              autoComplete="off" placeholder="contact@example.com" className={inputClass('email')} />
            {errors.email && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">Phone <span className="text-red-500">*</span></label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500 dark:text-gray-400 pointer-events-none">+91</span>
              <input value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                disabled={saving} inputMode="numeric" placeholder="9876543210" autoComplete="off"
                className={`${inputClass('phoneNumber')} pl-12`} />
            </div>
            {errors.phoneNumber && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.phoneNumber}</p>}
          </div>

          {errors.api && (
            <div className="px-3.5 py-2.5 rounded-lg text-sm bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300">
              {errors.api}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-gray-50 dark:bg-gray-800/60">
          <button type="button" onClick={onClose} disabled={saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            Send to Admin
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequestCustomerModal;
